import {
  fetchBaseQuery
} from '@reduxjs/toolkit/query/react';
import type {
  BaseQueryFn,
  BaseQueryApi,
} from '@reduxjs/toolkit/dist/query/baseQueryTypes';
import type {
  FetchArgs,
  FetchBaseQueryError,
  FetchBaseQueryMeta,
} from '@reduxjs/toolkit/dist/query/fetchBaseQuery';
import {
  incrementActiveRequests,
  decrementActiveRequests
} from '@shared/ui/circular-loader';
import {
  BRAND_ID,
  CMS_API_URL,
} from '../config';

type RootState = {
  ['session']: { sessionKey: Maybe<string>; },
  ['check-app-version']: { appVersion: Maybe<string>; }
};

export const baseQuery: BaseQueryFn<
  string | FetchArgs,
  unknown,
  FetchBaseQueryError,
  {},
  FetchBaseQueryMeta
> = fetchBaseQuery({
  baseUrl: CMS_API_URL,
  prepareHeaders(headers, { getState }) {
    const state = getState() as RootState;
    const appVersion = state['check-app-version']?.appVersion;
    if (appVersion) headers.set('X-App-Version', appVersion);
    return headers;
  },
});

type StrapiBaseQueryExtraOptions = {
  auth?: boolean;
  showLoader?: boolean;
  includeBrandId?: boolean;
};

export const baseStrapiQuery = async (
  arg: string | FetchArgs,
  api: BaseQueryApi,
  extraOptions: StrapiBaseQueryExtraOptions = { showLoader: true, includeBrandId: true }
) => {
  let newArgs = arg;

  if (typeof newArgs !== 'string' && !newArgs.params) {
    newArgs = { ...newArgs, params: {} };
  }

  if (extraOptions?.auth && typeof newArgs !== 'string') {
    const rootState = api.getState() as RootState;
    const sessionKey = rootState?.session?.sessionKey;

    newArgs.params = {
      ...newArgs.params,
      ...(sessionKey && { sessionKey }),
    };
  }

  if (extraOptions?.includeBrandId !== false && typeof newArgs !== 'string') {
    newArgs.params = {
      ...newArgs.params,
      brandId: BRAND_ID,
    };
  }

  const shouldShowLoader = extraOptions?.showLoader ?? true;
  if (shouldShowLoader) api.dispatch(incrementActiveRequests());

  try { return await baseQuery(newArgs, api, {}); }
  catch (error) { return { error }; }
  finally {
    if (shouldShowLoader) api.dispatch(decrementActiveRequests());
  }
};